import {
  AcceptUserAgreementsError,
  AcceptUserAgreementsResult,
  AcceptUserAgreementsResultTypenames,
  AcceptUserAgreementsSuccess,
  AdvanceBackupPaymentMethods,
  AdvanceBackupPaymentMethodsError,
  AdvanceBackupPaymentMethodsResult,
  AdvanceBackupPaymentMethodsResultTypenames,
  AdvanceExternalPaymentVerificationLinkCard,
  AdvanceExternalPaymentVerificationResult,
  AdvanceExternalPaymentVerificationResultTypenames,
  LendingDisbursementPaymentOptionsResult,
  LendingDisbursementPaymentOptionsResultTypenames,
  LendingDisbursementPaymentOptionsSuccess,
  LendingPaymentOptionDetails,
  LendingPaymentSource,
  LendingPaymentSourceDisplayGroup,
  PaymentSourceInput,
  SubmitManualAdvancePaymentError,
  SubmitManualAdvancePaymentResult,
  SubmitManualAdvancePaymentResultTypenames,
  ToggleAddDebitCardVisibilityError,
  ToggleAddDebitCardVisibilityResult,
  ToggleAddDebitCardVisibilityResultTypenames,
  ToggleAddDebitCardVisibilitySuccess,
  VaroAdvanceProductsError,
  VaroAdvanceProductsNotQualified,
  VaroAdvanceProductsPendingEligibility,
  VaroAdvanceProductsQualified,
  VaroAdvanceProductsResult,
  VaroAdvanceProductsResultTypenames,
} from '@varo/graphql-schema-types';
import { parseISO } from 'date-fns/parseISO';
import { formatDate } from 'shared/utils/string/date';

export const formatISODate = (date: string, format: string) => {
  return date ? formatDate(parseISO(date), format) : '';
};

export const isAcceptUserAgreementSuccess = (
  userAgreement: AcceptUserAgreementsResult | undefined,
): userAgreement is AcceptUserAgreementsSuccess => {
  return (
    userAgreement?.__typename ===
    AcceptUserAgreementsResultTypenames.AcceptUserAgreementsSuccess
  );
};

export const isAcceptUserAgreementGqlError = (
  userAgreement: AcceptUserAgreementsResult | undefined,
): userAgreement is AcceptUserAgreementsError => {
  return (
    userAgreement?.__typename ===
      AcceptUserAgreementsResultTypenames.AcceptUserAgreementsError ||
    // eslint-disable-next-line @typescript-eslint/dot-notation -- union member access; tsc requires bracket notation
    userAgreement?.['code'] !== undefined
  );
};

export const isVaroAdvanceProductsQualified = (
  advanceProductsData: VaroAdvanceProductsResult | undefined,
): advanceProductsData is VaroAdvanceProductsQualified => {
  return (
    advanceProductsData?.__typename ===
    VaroAdvanceProductsResultTypenames.VaroAdvanceProductsQualified
  );
};

export const isVaroAdvanceProductsNotQualified = (
  advanceProductsData: VaroAdvanceProductsResult | undefined,
): advanceProductsData is VaroAdvanceProductsNotQualified => {
  return (
    advanceProductsData?.__typename ===
    VaroAdvanceProductsResultTypenames.VaroAdvanceProductsNotQualified
  );
};

export const isVaroAdvanceProductsError = (
  advanceProductsData: VaroAdvanceProductsResult | undefined,
): advanceProductsData is VaroAdvanceProductsError => {
  return (
    advanceProductsData?.__typename ===
    VaroAdvanceProductsResultTypenames.VaroAdvanceProductsError
  );
};

export const isVaroAdvanceProductsPending = (
  advanceProductsData: VaroAdvanceProductsResult | undefined,
): advanceProductsData is VaroAdvanceProductsPendingEligibility => {
  return (
    advanceProductsData?.__typename ===
    VaroAdvanceProductsResultTypenames.VaroAdvanceProductsPendingEligibility
  );
};

export const isAdvanceBackupPaymentSuccess = (
  advanceBackupPaymentMethodsResponse:
    AdvanceBackupPaymentMethodsResult | undefined,
): advanceBackupPaymentMethodsResponse is AdvanceBackupPaymentMethods => {
  return (
    advanceBackupPaymentMethodsResponse?.__typename ===
      AdvanceBackupPaymentMethodsResultTypenames.AdvanceBackupPaymentMethods ||
    // eslint-disable-next-line @typescript-eslint/dot-notation -- union member access; tsc requires bracket notation
    advanceBackupPaymentMethodsResponse?.['methods'] !== undefined
  );
};

export const isAdvanceBackupPaymentError = (
  advanceBackupPaymentMethodsResponse:
    AdvanceBackupPaymentMethodsResult | undefined,
): advanceBackupPaymentMethodsResponse is AdvanceBackupPaymentMethodsError => {
  return (
    advanceBackupPaymentMethodsResponse?.__typename ===
      AdvanceBackupPaymentMethodsResultTypenames.AdvanceBackupPaymentMethodsError ||
    // eslint-disable-next-line @typescript-eslint/dot-notation -- union member access; tsc requires bracket notation
    advanceBackupPaymentMethodsResponse?.['code'] !== undefined
  );
};

export const isExternalPaymentLinkCardType = (
  value: AdvanceExternalPaymentVerificationResult | undefined,
): value is AdvanceExternalPaymentVerificationLinkCard =>
  value?.__typename ===
  AdvanceExternalPaymentVerificationResultTypenames.AdvanceExternalPaymentVerificationLinkCard;

export const isLendingDisbursementPaymentOptionsSuccess = (
  lendingDisbursementPaymentOptions:
    LendingDisbursementPaymentOptionsResult | undefined,
): lendingDisbursementPaymentOptions is LendingDisbursementPaymentOptionsSuccess => {
  return (
    lendingDisbursementPaymentOptions?.__typename ===
    LendingDisbursementPaymentOptionsResultTypenames.LendingDisbursementPaymentOptionsSuccess
  );
};

export const isToggleAddDebitCardVisibilitySuccess = (
  response: ToggleAddDebitCardVisibilityResult | undefined,
): response is ToggleAddDebitCardVisibilitySuccess => {
  return (
    response?.__typename ===
    ToggleAddDebitCardVisibilityResultTypenames.ToggleAddDebitCardVisibilitySuccess
  );
};

export const isToggleAddDebitCardVisibilityError = (
  result: ToggleAddDebitCardVisibilityResult | undefined,
): result is ToggleAddDebitCardVisibilityError => {
  return (
    result?.__typename ===
      ToggleAddDebitCardVisibilityResultTypenames.ToggleAddDebitCardVisibilityError ||
    // eslint-disable-next-line @typescript-eslint/dot-notation -- union member access; tsc requires bracket notation
    result?.['code'] !== undefined
  );
};

/**
 * Finds and returns the external debit card option from account options data
 * when coming from the debit card flow.
 * @param accountOptionsData The account options data containing payment options
 * @param fromDebitCardFlow String indicating if coming from debit card flow
 * @returns The external debit card payment option details if found, undefined otherwise
 */
export const getExternalDebitCardFromFlow = (
  accountOptionsData: {
    options?: Array<{
      displayGroup: LendingPaymentSourceDisplayGroup;
      details: LendingPaymentOptionDetails[];
    }>;
  },
  fromDebitCardFlow?: string,
): LendingPaymentOptionDetails | undefined => {
  if (fromDebitCardFlow === 'true') {
    const debitCardGroup = accountOptionsData.options?.find(
      (option) =>
        option.displayGroup === LendingPaymentSourceDisplayGroup.DebitCard,
    );
    const externalDebitCardDetail = debitCardGroup?.details.find(
      (detail) => detail.source === LendingPaymentSource.ExternalDebitCard,
    );
    return externalDebitCardDetail;
  }
  return undefined;
};

/**
 * Maps a LendingPaymentSource to PaymentSourceInput for payment mutations.
 * @param paymentSource The lending payment source to map
 * @returns The corresponding PaymentSourceInput value
 */
export const mapLendingPaymentSourceToPaymentSourceInput = (
  paymentSource: LendingPaymentSource,
): PaymentSourceInput => {
  const paymentSourceMapping: Record<LendingPaymentSource, PaymentSourceInput> =
    {
      [LendingPaymentSource.InternalDda]: PaymentSourceInput.InternalDda,
      [LendingPaymentSource.InternalSavings]:
        PaymentSourceInput.InternalSavings,
      [LendingPaymentSource.ExternalDebitCard]:
        PaymentSourceInput.ExternalDebitCard,
      [LendingPaymentSource.ExternalAch]: PaymentSourceInput.ExternalAch,
    };

  return paymentSourceMapping[paymentSource];
};

/**
 * Maps a LendingPaymentSource to its corresponding string representation.
 * @param paymentSource The lending payment source to map
 * @returns The corresponding string value
 */
export const mapLendingPaymentSourceToString = (
  paymentSource: LendingPaymentSource,
): string => {
  const paymentSourceMapping: Record<LendingPaymentSource, string> = {
    [LendingPaymentSource.ExternalAch]: 'ach',
    [LendingPaymentSource.ExternalDebitCard]: 'debit_card',
    [LendingPaymentSource.InternalSavings]: 'varo_savings',
    [LendingPaymentSource.InternalDda]: 'varo_checking',
  };

  return paymentSourceMapping[paymentSource];
};

export const isSubmitManualAdvancePaymentError = (
  paymentResult: SubmitManualAdvancePaymentResult | undefined,
): paymentResult is SubmitManualAdvancePaymentError => {
  return (
    paymentResult?.__typename ===
    SubmitManualAdvancePaymentResultTypenames.SubmitManualAdvancePaymentError
  );
};
